import { useEffect, useRef, useState } from "react";
import { IoMicOutline, IoStopOutline, IoTrashOutline, IoSendOutline } from "react-icons/io5";
import { toast } from "sonner";
import api from "@/api";

const fmt = (s) => {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

/**
 * Bottom sheet recorder — captures a short voice memo and, on send, uploads it
 * via /api/uploads returning { file_id, duration } to the parent.
 */
export function AudioRecorder({ open, onCancel, onReady }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState(null);
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const chunksRef = useRef([]);
  const recorderRef = useRef(null);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setElapsed(0); setBlob(null); setPreview(""); setRecording(false);
    return () => stop();
  }, [open]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const b = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
        setBlob(b);
        setPreview(URL.createObjectURL(b));
      };
      rec.start(250);
      recorderRef.current = rec;
      setRecording(true);
      const started = Date.now();
      timerRef.current = setInterval(() => setElapsed((Date.now() - started) / 1000), 200);
    } catch (e) {
      toast.error("Impossibile accedere al microfono");
      onCancel?.();
    }
  };

  const stop = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    try { recorderRef.current?.stop(); } catch { /* noop */ }
    recorderRef.current = null;
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    streamRef.current = null;
    setRecording(false);
  };

  const discard = () => { stop(); setBlob(null); setPreview(""); setElapsed(0); };

  const send = async () => {
    if (!blob) return;
    setUploading(true);
    try {
      const fd = new FormData();
      const ext = blob.type.includes("webm") ? "webm" : "m4a";
      fd.append("file", blob, `audio-${Date.now()}.${ext}`);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onReady?.({ file_id: data.id, duration: elapsed });
    } catch {
      toast.error("Upload audio fallito");
    } finally { setUploading(false); }
  };

  if (!open) return null;
  return (
    <div className="tf-sheet-backdrop" data-testid="chat-audio-recorder">
      <div className="tf-sheet" onClick={(e) => e.stopPropagation()}>
        <div style={{ textAlign: "center", padding: "10px 0 18px" }}>
          <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 6 }}>
            {recording ? "Registrazione in corso…" : preview ? "Anteprima" : "Registra audio"}
          </div>
          <div style={{ fontVariantNumeric: "tabular-nums", fontSize: 32, fontWeight: 600, color: recording ? "var(--tf-red)" : "var(--tf-text)" }}>
            {fmt(elapsed)}
          </div>
        </div>
        {preview && (
          <audio controls src={preview} style={{ width: "100%", marginBottom: 12 }} data-testid="audio-preview" />
        )}
        <div style={{ display: "grid", gridTemplateColumns: recording || !preview ? "1fr 1fr" : "1fr 1fr 1fr", gap: 10 }}>
          {!preview && !recording && (
            <>
              <button type="button" className="tf-btn-ghost" onClick={onCancel} data-testid="audio-cancel">Annulla</button>
              <button type="button" className="tf-btn-primary" onClick={start} data-testid="audio-start">
                <IoMicOutline size={18} /> Avvia
              </button>
            </>
          )}
          {recording && (
            <>
              <button type="button" className="tf-btn-ghost" onClick={() => { stop(); onCancel?.(); }} data-testid="audio-abort">Annulla</button>
              <button type="button" className="tf-btn-primary" onClick={stop} data-testid="audio-stop">
                <IoStopOutline size={18} /> Stop
              </button>
            </>
          )}
          {preview && !recording && (
            <>
              <button type="button" className="tf-btn-ghost" onClick={discard} data-testid="audio-discard">
                <IoTrashOutline size={16} /> Elimina
              </button>
              <button type="button" className="tf-btn-ghost" onClick={onCancel} data-testid="audio-cancel2">Annulla</button>
              <button type="button" className="tf-btn-primary" onClick={send} disabled={uploading} data-testid="audio-send">
                <IoSendOutline size={16} /> {uploading ? "Invio…" : "Invia"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
